/**
 * Viewport width as React state.
 *
 * CSS answers "how should this look at this width" on its own. This file is for
 * the other question — "should this even be here at this width" — which CSS
 * cannot answer, because a component hidden by `display: none` is still mounted,
 * still running its timers, still sending its requests.
 *
 * Layer rule: logic only. No styling decisions here.
 *
 * 把视口宽度变成 React 状态。
 *
 * 「这个宽度下它该长什么样」CSS 自己就能回答。本文件管的是另一个问题——
 * 「这个宽度下它到底该不该存在」——那个问题 CSS 回答不了：被 `display: none`
 * 藏起来的组件依然挂载着、依然在跑它的定时器、依然在发它的请求。
 * 分层规则：仅逻辑，不含任何样式决策。
 */

import { useEffect, useState } from 'react'

/**
 * The phone boundary.
 *
 * 767px because that is already this project's phone edge — skin.css switches
 * layout there in five places and tokens.css changes the header height there.
 * A second, nearby breakpoint invented here would put the console's cut-off
 * one pixel away from the stylesheet's, which is the kind of disagreement
 * nobody finds until a screen is exactly 768px wide.
 *
 * Deliberately a width query and not `(pointer: coarse)`: a tablet in landscape
 * is coarse and edits perfectly well, and a desktop monitor with a touchscreen
 * would be caught by it for no reason. Width is what actually decides whether
 * the editor fits.
 *
 * 手机边界。
 *
 * 取 767px，因为它本来就是本项目的手机边界——skin.css 有五处在这里切换布局，
 * tokens.css 也在这里改页头高度。若在这里另发明一个相近的断点，
 * 控制台的分界线就会和样式表的差一个像素；这种分歧没人会发现，
 * 直到某块屏幕恰好宽 768px。
 *
 * 刻意用宽度查询而不是 `(pointer: coarse)`：平板横屏是 coarse，却完全能编辑；
 * 而带触摸屏的桌面显示器会被无端误伤。真正决定编辑器放不放得下的是宽度。
 */
export const NARROW_QUERY = '(max-width: 767px)'

/**
 * Subscribe to a media query.
 *
 * Subscribed, not sampled. Reading `innerWidth` once at mount would be wrong
 * the moment somebody rotates the phone, and polling `resize` is worse than it
 * looks: on iOS that event fires as the address bar collapses, and a pinch-zoom
 * changes the visual viewport without changing the layout viewport a media
 * query reads. `matchMedia` reports exactly what the stylesheet reports.
 *
 * The fallback when `matchMedia` is missing returns false — "not narrow". That
 * direction is chosen, not incidental: an absent API must not lock somebody out
 * of a console that would have worked.
 *
 * 订阅一个媒体查询。
 *
 * 是订阅，不是采样。挂载时读一次 `innerWidth`，在有人把手机转横的那一刻就错了；
 * 而轮询 `resize` 比看上去更糟：iOS 上地址栏收合会触发它，双指缩放又只改变视觉视口、
 * 不改变媒体查询所读的布局视口。`matchMedia` 报告的，正是样式表所报告的。
 *
 * `matchMedia` 缺失时的兜底返回 false（「不窄」）。这个方向是选定的而非随手写的：
 * 一个缺失的 API 不该把人挡在「本来能用」的后台之外。
 *
 * @param {string} query A CSS media query / 一条 CSS 媒体查询。
 * @returns {boolean} Whether it currently matches / 当前是否匹配。
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(query).matches)
  )

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const list = window.matchMedia(query)
    // Re-read on subscribe: between the initial render and this effect the
    // window may already have changed, and the first `change` event would not
    // fire until it changes again.
    // 订阅时重新读一次：首次渲染与本 effect 之间窗口可能已经变过，
    // 而第一个 change 事件要等到「再变一次」才会到来。
    setMatches(list.matches)
    const onChange = (event) => setMatches(event.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/**
 * Whether the viewport is phone-width.
 *
 * 视口是否为手机宽度。
 *
 * @returns {boolean} True below the phone boundary / 低于手机边界时为 true。
 */
export function useIsNarrow() {
  return useMediaQuery(NARROW_QUERY)
}
